import { useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useTheme } from '@/hooks/useTheme';
import { useAuth } from '@/hooks/useAuth';
import { MaterialColorsCard } from '@/components/MaterialColorsCard';

function getNextResetDate(usageResetMonth) {
  if (!usageResetMonth) return null;
  const [year, month] = usageResetMonth.split('-').map(Number);
  if (!year || !month) return null;
  const nextMonth = month === 12 ? 1 : month + 1;
  const nextYear = month === 12 ? year + 1 : year;
  const date = new Date(nextYear, nextMonth - 1, 1);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const FREE_TIER_LIMIT = 50;

export function Settings() {
  const { theme, toggleTheme } = useTheme();
  const { userInfo, refreshUserInfo } = useAuth();

  useEffect(() => {
    refreshUserInfo();
  }, []);

  const queriesUsed = userInfo?.usageCurrentMonth ?? 0;
  const usageResetMonth = userInfo?.usageResetMonth ?? '';
  const nextReset = getNextResetDate(usageResetMonth);
  const progressPct = Math.min((queriesUsed / FREE_TIER_LIMIT) * 100, 100);

  return (
    <div className="container mx-auto p-6 max-w-2xl space-y-6">
      <div className="mb-2">
        <h1 className="text-2xl font-semibold">Settings</h1>
        <p className="text-muted-foreground">Manage your preferences and account</p>
      </div>

      {/* Appearance */}
      <Card>
        <CardHeader>
          <CardTitle>Appearance</CardTitle>
          <CardDescription>Customize how FastenAI looks</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Dark mode</p>
              <p className="text-xs text-muted-foreground">
                Currently {theme === 'dark' ? 'on' : 'off'}
              </p>
            </div>
            <button
              onClick={toggleTheme}
              aria-label="Toggle dark mode"
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                theme === 'dark' ? 'bg-primary' : 'bg-muted'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-background shadow-sm transition-transform ${
                  theme === 'dark' ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </CardContent>
      </Card>

      <MaterialColorsCard />

      {/* Usage */}
      <Card>
        <CardHeader>
          <CardTitle>Usage</CardTitle>
          <CardDescription>Your query usage this month</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-end justify-between">
            <div>
              <p className="text-3xl font-bold">{queriesUsed}</p>
              <p className="text-sm text-muted-foreground">queries this month</p>
            </div>
            <p className="text-sm text-muted-foreground text-right">
              of {FREE_TIER_LIMIT} free tier limit
            </p>
          </div>

          <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
            <div
              className="h-full rounded-full bg-primary transition-all"
              style={{ width: `${progressPct}%` }}
            />
          </div>

          <p className="text-xs text-muted-foreground">
            Limit not enforced during beta.
            {nextReset && ` Resets ${nextReset}.`}
          </p>

          <div className="rounded-lg border border-primary/20 bg-primary/5 p-3">
            <p className="text-sm text-muted-foreground">
              You&apos;re on the{' '}
              <span className="font-medium text-foreground">free beta plan</span>.
              Enjoy unlimited queries during beta — no credit card required.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
